import {getSiteUrl} from "@/helpers/functions";

export type WebPageType =
    | "WebPage"
    | "AboutPage"
    | "ContactPage"
    | "TechArticle"
    | "CollectionPage"
    | "ItemPage";

export interface SeoSchemaWebPageProps {
    pageType?: WebPageType;
    url: string;
    name: string;
    description: string;
    datePublished?: string;
    dateModified?: string;
    primaryImageUrl?: string;
    breadcrumbId?: string;
    inLanguage?: string;
    author?: {name: string; url?: string};
    publisherRef?: boolean;
}

const toIso = (value?: string): string | undefined => {
    if (!value) return undefined;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return undefined;
    return d.toISOString();
};

const toAbsolute = (siteUrl: string, url: string): string => {
    if (/^https?:\/\//i.test(url)) return url;
    return `${siteUrl}${url.startsWith("/") ? "" : "/"}${url}`;
};

export function SeoSchemaWebPage({
    pageType = "WebPage",
    url,
    name,
    description,
    datePublished,
    dateModified,
    primaryImageUrl,
    breadcrumbId,
    inLanguage = "en-US",
    author,
    publisherRef = true,
}: SeoSchemaWebPageProps) {
    const siteUrl = getSiteUrl();
    const absoluteUrl = toAbsolute(siteUrl, url);
    const orgRef = {"@id": `${siteUrl}/#organization`};
    const published = toIso(datePublished);
    const modified = toIso(dateModified) ?? published;

    const schema: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": pageType,
        "@id": `${absoluteUrl}#webpage`,
        "url": absoluteUrl,
        "name": name,
        "description": description,
        "isPartOf": {"@id": `${siteUrl}/#website`},
        "inLanguage": inLanguage,
    };

    if (publisherRef) schema.publisher = orgRef;
    if (published) schema.datePublished = published;
    if (modified) schema.dateModified = modified;
    if (primaryImageUrl) {
        schema.primaryImageOfPage = {
            "@type": "ImageObject",
            "url": toAbsolute(siteUrl, primaryImageUrl),
        };
    }
    if (breadcrumbId) {
        schema.breadcrumb = {"@id": breadcrumbId};
    }
    if (author) {
        schema.author = {
            "@type": "Person",
            "name": author.name,
            ...(author.url ? {url: toAbsolute(siteUrl, author.url)} : {}),
        };
    }

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{__html: JSON.stringify(schema)}}
        />
    );
}
